// Wohler Chemical — Cognitive Enhancement + Wakefulness (Programs C/J)

export const cognitiveCompounds = {
  "f-modafinil": {
    id: "f-modafinil",
    name: "F-Modafinil (CRL-40,940)",
    fullName: "Bisfluoromodafinil / Flmodafinil",
    aliases: ["Flmodafinil", "CRL-40,940", "Lauflumide"],
    categoryId: "cognitive",
    program: "Program C",
    cas: "90280-13-0",
    formula: "C₁₅H₁₃F₂NO₂S",
    molecularWeight: "309.33 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Parent compound modafinil is Schedule IV. F-modafinil retains structural similarity — Schedule IV parent relationship creates medium scheduling risk. FDA/Rx-drug adjacency (modafinil is approved drug) requires careful research-only positioning.",
    benefitScore: 78,
    harmScore: 28,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — with legal review + strong EHS process controls",
    summary: "Bisfluoro analog of modafinil developed by Cephalon/Lafon (CRL-40,940). Reported in animal studies as 3–5× more potent than modafinil with longer duration. Wakefulness-promoting with likely monoamine/orexin/inflammatory-pathway relevance. Wohler internal scale-up safety memo identifies acid-chloride route as the kg-scale limitation (HCl/SO₂ from SOCl₂ or HCl/CO/CO₂ from oxalyl chloride) — requires acid-gas scrubber + ventilated containment + CO monitor.",
    pros: [
      "3–5× more potent than modafinil with extended duration",
      "Novel wakefulness-promoting research tool; potential for ADHD-adjacent and fatigue research",
      "Anti-inflammatory profile suggested by related derivatives (BV2 microglia studies)",
      "Published synthesis routes (Jung 2012, Cephalon patent)",
      "Wohler has existing process development on this compound"
    ],
    cons: [
      "Schedule IV parent (modafinil) creates scheduling-trajectory risk",
      "Acid-chloride kg-scale hazards (HCl, SO₂, CO) — scrubber + CO monitor required",
      "FDA/Rx-drug adjacency — clear 'research use only' positioning essential",
      "Dependence potential (class effect — modafinil has low but non-zero abuse liability)"
    ],
    pharmacology: {
      targets: [
        { receptor: "Dopamine transporter (DAT)", activity: "Weak inhibitor", value: "Primary wakefulness mechanism hypothesized" },
        { receptor: "Orexin system", activity: "Indirect activator", value: "Contributes to wakefulness" },
        { receptor: "iNOS / COX-2 (microglia)", activity: "Downregulator (from related modafinil analogs)", value: "Anti-inflammatory angle" }
      ],
      duration: "12–18 h (vs ~10 h for modafinil)",
      halfLife: "Estimated 12–15 h",
      metabolism: "Hepatic CYP3A4 — slower than modafinil due to fluorine protection against metabolism",
      mechanism: "Wakefulness-promoting — DAT inhibition, orexin activation, histamine release. Fluorine substitution increases potency and metabolic stability."
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis (50 g)",
      overview: "Based on Jung et al. 2012 and Cephalon patent. Key steps: S-alkylation of thiourea analog → H₂O₂ oxidation to sulfoxide → acid-chloride activation → amide formation.",
      steps: [
        { step: "1", title: "Bis(4-fluorophenyl) methyl bromide", description: "Bromination of 4,4'-bis(fluoro)diphenylmethane via NBS/AIBN in CCl₄ (substitute: EtOAc with benzoyl peroxide).", solvent: "Ethyl Acetate (replacing CCl₄)", keyReagents: "NBS, benzoyl peroxide" },
        { step: "2", title: "S-Alkylation", description: "Thioglycolic acid + bis(4-F-phenyl)methyl bromide + base (K₂CO₃) in EtOH → bis(4-fluoro-benzhydryl)thio-acetic acid.", solvent: "Ethanol", keyReagents: "Thioglycolic acid, K₂CO₃" },
        { step: "3", title: "H₂O₂ oxidation to sulfoxide", description: "Oxidation with 30 % H₂O₂ in acetic acid / methanol, 0 °C → RT → sulfoxide intermediate.", solvent: "Methanol / acetic acid", keyReagents: "H₂O₂ (30 %)" },
        { step: "4", title: "Acid-chloride formation", description: "SOCl₂ or oxalyl chloride (1.2 eq) in EtOAc or THF, 0 °C. ⚠️ Releases HCl + SO₂ (SOCl₂) or HCl + CO + CO₂ (oxalyl chloride). Scrubber required even at lab scale.", solvent: "Ethyl Acetate", keyReagents: "SOCl₂ or (COCl)₂" },
        { step: "5", title: "Amide formation", description: "Add aqueous NH₃ or NH₄OH dropwise at 0 °C → F-modafinil amide.", solvent: "EtOAc/aqueous NH₃", keyReagents: "NH₃/NH₄OH" },
        { step: "6", title: "Crystallization", description: "Recrystallize from ethanol/water or EtOAc/heptane.", solvent: "Ethanol/water or EtOAc/Heptane", keyReagents: "Ethanol, water" }
      ],
      yield: "35–50 % over 5 steps",
      purityTarget: "≥ 99 % HPLC"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan (1 kg F-Modafinil)",
      feasibility: "Moderate — acid-chloride step is the limiting factor. Requires engineering controls per Wohler Chemical scale-up safety memo.",
      solventsUsed: ["Ethyl Acetate", "Ethanol", "Methanol", "THF", "Acetic Acid (process aid)", "Heptane"],
      equipment: [
        "200 L jacketed glass-lined reactor for step-4 acid-chloride activation",
        "Acid-gas scrubber train — NaOH (for HCl + SO₂) + CaCO₃ (backup)",
        "Carbon monoxide (CO) monitor with alarm (if oxalyl chloride route) — per Wohler F-mod scale-up memo",
        "Ventilated process hood (minimum 100 ft/min face velocity) for acid-chloride addition",
        "Protected vacuum pump (PTFE-lined) for solvent removal after acid-chloride step",
        "Enclosed rotary evaporator with condensate trap",
        "H₂O₂ addition skid with temperature-trip interlock (ΔT > 10 °C triggers abort)",
        "Ammonia gas handling — scrubbed vent + gas detection",
        "Crystallizer with programmable cooling + wash station"
      ],
      batchPlan: {
        targetBatch: "1.0 kg F-modafinil free amide",
        startingMaterial: "1.4 kg bis(4-F-phenyl)methyl bromide (synthesized from 1.1 kg 4,4'-bis-fluoro-diphenylmethane)",
        keyVolumes: [
          "EtOAc (bromination): 12 L",
          "Ethanol (S-alkylation): 15 L",
          "Thioglycolic acid: 0.5 kg",
          "K₂CO₃: 0.75 kg",
          "H₂O₂ (30 %): 1.2 L",
          "Acetic acid: 2 L (oxidation)",
          "Methanol: 8 L (oxidation)",
          "Oxalyl chloride: 0.6 kg (1.2 eq for acid-chloride step) OR SOCl₂: 0.6 kg",
          "NH₃ (aqueous, 25 %): 2 L",
          "Ethanol/water crystallization: 8 L ethanol + 2 L water",
          "Heptane wash: 3 L"
        ]
      },
      hazards: [
        "⚠️ HCl + SO₂ generation (SOCl₂ route) — scrubber required; corrosive + respiratory hazard",
        "⚠️ HCl + CO + CO₂ generation (oxalyl chloride route) — scrubber + CO monitor required (per Wohler memo)",
        "H₂O₂ oxidation — exothermic; thermal runaway risk; ΔT > 10 °C trip required",
        "Ammonia gas — toxic by inhalation; scrubbed vent required",
        "Thioglycolic acid — strong malodor, skin sensitizer, wear PPE",
        "EtOAc flammable — ATEX zone 1",
        "Bromination products — lachrymators and skin irritants"
      ],
      vendorNotes: [
        "4,4'-Bis-fluoro-diphenylmethane: Sigma, Combi-Blocks, Matrix Scientific",
        "Thioglycolic acid: Sigma, Fisher",
        "H₂O₂ (30 %): Solvay, Peroxy Corp — bulk supply",
        "Oxalyl chloride: Sigma, Thermo — 1–5 kg bottles",
        "SOCl₂: Sigma, Bayer — bulk (prefer over oxalyl chloride to avoid CO hazard unless process requires)"
      ],
      estCostPerKg: "$18,000 – $28,000 (includes scrubber maintenance + CO monitor + PPE allowances)",
      standardSolventAlternatives: "All primary Wohler standard solvents used directly. No non-standard solvents in core process."
    },
    safety: {
      acuteToxicity: "Rat LD50 ~2200 mg/kg oral (modafinil comparator); F-modafinil assumed similar or better",
      chronicConcerns: [
        "Wakefulness compounds — sleep deprivation effects with chronic use",
        "Low but non-zero dependence/abuse potential (DAT inhibition)",
        "Rare SJS/TEN (Stevens-Johnson) reports with modafinil — class warning"
      ],
      contraindications: [
        "History of cutaneous drug reactions",
        "Severe hepatic impairment",
        "Left ventricular hypertrophy, mitral valve prolapse (class)",
        "Pregnancy"
      ],
      acuteSideEffects: [
        "Wakefulness, reduced fatigue perception",
        "Headache (most common; ~15 % in modafinil trials)",
        "Insomnia if taken late in day",
        "Anxiety, dry mouth, mild appetite suppression"
      ],
      ehsClassification: "GHS Cat. 4 oral; fine particle dust → respiratory PPE during powder handling"
    },
    publishedResearch: [
      { citation: "Jung JK et al. Simple Synthesis of Modafinil Derivatives and Their Anti-inflammatory Activity. Molecules. 2012;17(9):10446-10458.", url: "https://www.mdpi.com/1420-3049/17/9/10446" },
      { citation: "Wohler Chemical Internal. F-modafinil Executive Summary with Vendor Tables. 2026." },
      { citation: "Lafon L. Acetamide derivatives. US Patent 4,177,290 (1979) — modafinil scaffold (expired)." }
    ],
    patentInfo: "Modafinil composition patents expired. F-modafinil method/formulation and specific-indication patents available. Cephalon held CRL-40,940 development IP; status review needed.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 19, evidenceQuality: 17, unmetMedicalNeed: 17, novelty: 13, universityInterest: 12 },
      harmBreakdown: { acuteToxicity: 5, chronicToxicity: 7, dependence: 6, schedulingRisk: 10 },
      netValue: 50,
      confidenceRationale: "MODERATE — good published synthesis; Schedule IV parent creates trajectory concern."
    },
    priorityTier: "Tier 1 — Production target",
    nextSteps: [
      "Finalize scrubber train design for acid-chloride step",
      "Patent landscape review on F-modafinil IP",
      "Produce 50-g reference standard + COA",
      "Anti-inflammatory activity confirmation in BV2 microglia assay",
      "University partner identification for neuroinflammation research"
    ]
  },

  "sunifiram": {
    id: "sunifiram",
    name: "Sunifiram",
    fullName: "1-Benzoyl-4-propanoylpiperazine (DM-235)",
    aliases: ["DM-235"],
    categoryId: "cognitive",
    program: "Program J",
    cas: "314728-85-3",
    formula: "C₁₄H₁₈N₂O₂",
    molecularWeight: "246.31 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled.",
    benefitScore: 72,
    harmScore: 22,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — novel mechanism, low regulatory risk",
    summary: "Ampakine-class cognitive enhancer that potentiates AMPA receptors and stimulates CaMKII (LTP/memory consolidation). Also upregulates acetylcholine and dopamine in prefrontal cortex. Active at very low doses in rodent cognition assays (ng/kg range suggested in some reports).",
    pros: [
      "Novel mechanism distinct from classic stimulants — low dependence liability",
      "AMPA potentiation directly relevant to Alzheimer's / cognitive impairment research",
      "Unscheduled, low regulatory risk",
      "Simple piperazine-based synthesis"
    ],
    cons: ["Human clinical data very limited", "High potency in rodent models requires careful dose-response characterization"],
    pharmacology: {
      targets: [
        { receptor: "AMPA (GluA)", activity: "Allosteric potentiator", value: "Enhances glutamate transmission" },
        { receptor: "CaMKII", activity: "Activator", value: "LTP key enzyme" },
        { receptor: "NMDA (secondary)", activity: "Modulator", value: "Minor" }
      ],
      duration: "Short (~2 h)",
      mechanism: "AMPA potentiation → LTP → cognitive enhancement; distinct from amphetamine/methylphenidate mechanism"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Two-step amide formation on piperazine scaffold.",
      steps: [
        { step: "1", title: "Mono-benzoylation", description: "Piperazine + benzoyl chloride (1.0 eq) + Et₃N in THF or EtOAc, 0 °C → RT, 2 h.", solvent: "THF or Ethyl Acetate", keyReagents: "Benzoyl chloride, Et₃N" },
        { step: "2", title: "Propanoylation", description: "Mono-benzoyl piperazine + propanoyl chloride (1.1 eq) + Et₃N, 0 °C → RT.", solvent: "EtOAc", keyReagents: "Propanoyl chloride, Et₃N" },
        { step: "3", title: "Crystallization", description: "Ethanol/heptane.", solvent: "Ethanol/Heptane", keyReagents: "Ethanol, heptane" }
      ],
      yield: "60–75 % over 2 steps",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "High — simple chemistry, well-studied",
      solventsUsed: ["Ethyl Acetate", "THF", "Ethanol", "Heptane"],
      equipment: ["Standard acylation reactor", "Crystallizer"],
      batchPlan: {
        targetBatch: "1 kg sunifiram",
        startingMaterial: "0.4 kg piperazine",
        keyVolumes: ["EtOAc: 15 L", "THF: 10 L", "Ethanol: 10 L", "Benzoyl chloride: 0.65 kg", "Propanoyl chloride: 0.48 kg", "Et₃N: 1.0 kg"]
      },
      hazards: ["Acid chloride HCl evolution — scrubber required", "Et₃N malodor"],
      vendorNotes: ["Piperazine: Sigma, BASF", "Acid chlorides: Sigma, Thermo"],
      estCostPerKg: "$8,500 – $12,000"
    },
    safety: {
      acuteToxicity: "Rodent LD50 > 500 mg/kg; excellent safety margin",
      chronicConcerns: ["AMPA potentiation — potential seizure threshold lowering at high doses"],
      contraindications: ["Seizure disorder", "Pregnancy"],
      acuteSideEffects: ["Cognitive enhancement", "Possible anxiety at high doses", "Mild stimulation"],
      ehsClassification: "GHS Cat. 5 oral (low toxicity)"
    },
    publishedResearch: [
      { citation: "Ghelardini C et al. DM232 (unifiram) and DM235 (sunifiram): new potent cognition enhancers. Behav Brain Res. 2002." },
      { citation: "Galeotti N et al. Sunifiram-induced anti-amnestic effects. Drug Dev Res. 2008." }
    ],
    patentInfo: "Composition patents expired; method-of-use and formulation patents available.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 18, evidenceQuality: 14, unmetMedicalNeed: 17, novelty: 13, universityInterest: 10 },
      harmBreakdown: { acuteToxicity: 3, chronicToxicity: 6, dependence: 3, schedulingRisk: 10 },
      netValue: 50,
      confidenceRationale: "MODERATE"
    },
    priorityTier: "Tier 2",
    nextSteps: ["Reference standard production", "Partner with cognition lab for rigorous replication"]
  },

  "unifiram": {
    id: "unifiram",
    name: "Unifiram",
    fullName: "(S)-1-(Benzoyl)-4-(propionyl)homopiperazine (DM-232)",
    aliases: ["DM-232"],
    categoryId: "cognitive",
    program: "Program J",
    formula: "C₁₅H₂₀N₂O₂",
    molecularWeight: "260.34 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled.",
    benefitScore: 70,
    harmScore: 22,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — pair with sunifiram for SAR",
    summary: "Homolog of sunifiram with homopiperazine (7-membered ring) in place of piperazine. AMPA potentiator with potentially enhanced selectivity. Anti-amnestic in scopolamine-induced amnesia rodent models.",
    pros: ["Paired comparison with sunifiram for SAR", "Anti-amnestic activity validated", "Simple synthesis", "Unscheduled"],
    cons: ["Less-studied than sunifiram", "Limited human data"],
    pharmacology: {
      targets: [
        { receptor: "AMPA", activity: "Allosteric potentiator", value: "Similar to sunifiram" },
        { receptor: "CaMKII", activity: "Activator", value: "LTP" }
      ],
      duration: "~2 h",
      mechanism: "AMPA-LTP cognitive enhancement"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Analogous to sunifiram with homopiperazine.",
      steps: [
        { step: "1", title: "Mono-benzoylation", description: "Homopiperazine + benzoyl chloride + Et₃N in EtOAc.", solvent: "Ethyl Acetate", keyReagents: "Benzoyl chloride, Et₃N" },
        { step: "2", title: "Propanoylation", description: "Propanoyl chloride + Et₃N.", solvent: "Ethyl Acetate", keyReagents: "Propanoyl chloride" },
        { step: "3", title: "Crystallization", description: "Ethanol/heptane.", solvent: "Ethanol/Heptane", keyReagents: "Ethanol, heptane" }
      ],
      yield: "60–72 %",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "High",
      solventsUsed: ["Ethyl Acetate", "THF", "Ethanol", "Heptane"],
      equipment: ["Same as sunifiram"],
      batchPlan: { targetBatch: "1 kg unifiram", startingMaterial: "0.45 kg homopiperazine", keyVolumes: ["EtOAc: 15 L"] },
      hazards: ["Same as sunifiram"],
      vendorNotes: ["Homopiperazine: Sigma, Combi-Blocks"],
      estCostPerKg: "$10,000 – $14,000"
    },
    safety: {
      acuteToxicity: "Favorable preclinical",
      chronicConcerns: ["Seizure threshold"],
      contraindications: ["Seizure disorder"],
      acuteSideEffects: ["Cognitive enhancement", "Mild stimulation"],
      ehsClassification: "GHS Cat. 5 oral"
    },
    publishedResearch: [
      { citation: "Ghelardini C et al. DM232 (unifiram) and DM235 (sunifiram). Behav Brain Res. 2002." }
    ],
    patentInfo: "Composition expired.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 17, evidenceQuality: 14, unmetMedicalNeed: 17, novelty: 12, universityInterest: 10 },
      harmBreakdown: { acuteToxicity: 3, chronicToxicity: 6, dependence: 3, schedulingRisk: 10 },
      netValue: 48,
      confidenceRationale: "MODERATE"
    },
    priorityTier: "Tier 2",
    nextSteps: ["Reference standard production"]
  },

  "prl-8-53": {
    id: "prl-8-53",
    name: "PRL-8-53",
    fullName: "Methyl 3-(2-(benzyl(methyl)amino)ethyl)benzoate hydrochloride",
    aliases: [],
    categoryId: "cognitive",
    program: "Program J",
    cas: "51352-87-5",
    formula: "C₁₈H₂₁NO₂·HCl",
    molecularWeight: "319.83 g/mol",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled.",
    benefitScore: 72,
    harmScore: 22,
    evidenceConfidence: "MODERATE",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — replicate the famous 1978 study",
    summary: "Dopaminergic/serotonergic modulator + cholinergic enhancer developed by Hansl. The 1978 Hansl & Mead clinical trial (Psychopharmacology) reported >200 % improvement in verbal learning retention in below-average baseline scorers — a dramatic result never formally replicated. Ideal rigorous-replication candidate for university research partnership.",
    pros: ["Dramatic 1978 human data — never replicated; publishable replication project", "Multi-mechanism pharmacology — dopaminergic/serotonergic/cholinergic", "Unscheduled", "Simple synthesis"],
    cons: ["Minimal modern data", "1978 study had small sample size — replication may not confirm"],
    pharmacology: {
      targets: [
        { receptor: "Dopamine (indirect)", activity: "Modulator", value: "Possibly DAT modulation" },
        { receptor: "Acetylcholine (indirect)", activity: "Enhancer", value: "Proposed cognitive mechanism" },
        { receptor: "Serotonin (indirect)", activity: "Modulator", value: "Secondary" }
      ],
      duration: "Short (~2 h subjective)",
      mechanism: "Multi-neurotransmitter modulation; exact mechanism incompletely understood"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Ester formation + amine chain installation.",
      steps: [
        { step: "1", title: "Methyl 3-(2-bromoethyl)benzoate", description: "From methyl 3-(2-hydroxyethyl)benzoate via bromination (PBr₃ or CBr₄/PPh₃).", solvent: "Ethyl Acetate or THF", keyReagents: "PBr₃" },
        { step: "2", title: "N-alkylation", description: "React with N-methyl-benzylamine in ethanol with K₂CO₃.", solvent: "Ethanol", keyReagents: "N-methyl-benzylamine, K₂CO₃" },
        { step: "3", title: "HCl salt", description: "HCl in ethanol/EtOAc.", solvent: "Ethanol/EtOAc", keyReagents: "HCl" }
      ],
      yield: "55–65 %",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "High",
      solventsUsed: ["Ethyl Acetate", "THF", "Ethanol", "Heptane"],
      equipment: ["Standard"],
      batchPlan: { targetBatch: "1 kg PRL-8-53", startingMaterial: "1 kg methyl 3-(2-hydroxyethyl)benzoate", keyVolumes: ["EtOAc: 12 L", "EtOH: 15 L"] },
      hazards: ["PBr₃ corrosive + moisture reactive"],
      vendorNotes: ["Methyl 3-(2-hydroxyethyl)benzoate: Sigma, Combi-Blocks"],
      estCostPerKg: "$9,500 – $13,000"
    },
    safety: {
      acuteToxicity: "Favorable preclinical",
      chronicConcerns: ["Long-term data absent"],
      contraindications: ["MAOI", "Pregnancy"],
      acuteSideEffects: ["Cognitive enhancement", "Possible mild stimulation"],
      ehsClassification: "GHS Cat. 5 oral"
    },
    publishedResearch: [
      { citation: "Hansl NR, Mead BT. PRL-8-53: enhanced learning and subsequent retention in humans as a result of low oral doses of new psychotropic agent. Psychopharmacology. 1978;56(3):249-53." }
    ],
    patentInfo: "Composition expired.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 18, evidenceQuality: 13, unmetMedicalNeed: 17, novelty: 14, universityInterest: 10 },
      harmBreakdown: { acuteToxicity: 3, chronicToxicity: 6, dependence: 3, schedulingRisk: 10 },
      netValue: 50,
      confidenceRationale: "MODERATE — dramatic historical data; replication is the story."
    },
    priorityTier: "Tier 1 — University replication story",
    nextSteps: ["Reference standard production", "Partner with university for rigorous double-blind replication of Hansl 1978"]
  },

  "nsi-189": {
    id: "nsi-189",
    name: "NSI-189",
    fullName: "(4-benzylpiperazin-1-yl)[2-(3-methylbutylamino)pyridin-3-yl]methanone phosphate",
    aliases: [],
    categoryId: "cognitive",
    program: "Program J",
    cas: "1270138-40-3",
    formula: "C₂₂H₃₀N₄O · H₃PO₄",
    molecularWeight: "464.49 g/mol (phosphate salt)",
    schedulingStatus: "unscheduled",
    schedulingNote: "Not federally scheduled. Failed Phase II for MDD (Neuralstem, 2017) but not approved or scheduled.",
    benefitScore: 75,
    harmScore: 20,
    evidenceConfidence: "HIGH",
    status: "Favored",
    statusColor: "green",
    goDecision: "GO — unique neurogenesis mechanism",
    summary: "Neurogenic agent that stimulates hippocampal neurogenesis, specifically increasing dentate gyrus volume. Distinct mechanism from all existing antidepressants. Failed Phase II primary endpoint but promising signals in depression with cognitive complaints and anhedonia. Existing Phase I/II human safety data makes it an unusually well-characterized unscheduled research compound.",
    pros: [
      "Unique neurogenesis mechanism — no competing compounds",
      "Existing Phase I/II human safety data (rare for an unscheduled compound)",
      "Strong cognitive + antidepressant research rationale",
      "Applicable to TBI, Alzheimer's, cognitive decline research",
      "IP landscape open (Neuralstem shelved program)"
    ],
    cons: ["Failed Phase II primary endpoint — clinical signal is secondary-endpoint only", "Complex multi-step synthesis"],
    pharmacology: {
      targets: [
        { receptor: "Neurogenesis pathway (exact target not fully mapped)", activity: "Stimulator", value: "Increases DG volume and hippocampal neurogenesis" }
      ],
      duration: "Daily dosing required for neurogenic effect (weeks)",
      mechanism: "Neurogenesis stimulation — novel mechanism distinct from monoamine modulators"
    },
    labSynthesis: {
      title: "Laboratory-Scale Synthesis",
      overview: "Multi-step: pyridine intermediate + piperazine amide coupling + phosphate salt formation.",
      steps: [
        { step: "1", title: "2-Amino-3-carboxylate pyridine intermediate", description: "Install 3-methylbutylamine on 2-chloronicotinic acid.", solvent: "THF or EtOAc", keyReagents: "2-chloronicotinic acid, isoamylamine" },
        { step: "2", title: "Piperazine amide coupling", description: "Activate carboxylate (HATU or acid chloride) + 4-benzylpiperazine.", solvent: "THF/EtOAc", keyReagents: "4-benzylpiperazine, coupling reagent" },
        { step: "3", title: "Phosphate salt", description: "Free base + H₃PO₄ in ethanol/water.", solvent: "Ethanol/water", keyReagents: "H₃PO₄" }
      ],
      yield: "25–40 % over 3 steps",
      purityTarget: "≥ 99 %"
    },
    kgScaleSynthesis: {
      title: "Kilogram-Scale Plan",
      feasibility: "Moderate — 3–4 step synthesis feasible on Wohler standard equipment",
      solventsUsed: ["Ethyl Acetate", "THF", "Ethanol", "Methanol", "Heptane"],
      equipment: ["Standard multi-step reactor", "Crystallizer"],
      batchPlan: {
        targetBatch: "1 kg NSI-189 phosphate",
        startingMaterial: "1 kg 2-chloronicotinic acid",
        keyVolumes: ["THF: 15 L", "EtOAc: 12 L", "Ethanol: 10 L"]
      },
      hazards: ["Standard synthesis hazards; coupling reagents can sensitize"],
      vendorNotes: ["2-Chloronicotinic acid: Sigma, Combi-Blocks", "Benzylpiperazine: BASF"],
      estCostPerKg: "$25,000 – $38,000"
    },
    safety: {
      acuteToxicity: "Human Phase I data — acceptable safety at 40–80 mg/day",
      chronicConcerns: ["Long-term neurogenesis effects unknown"],
      contraindications: ["Pregnancy", "Uncontrolled epilepsy (theoretical concern)"],
      acuteSideEffects: ["Headache", "Nausea", "Generally mild"],
      ehsClassification: "GHS Cat. 4 oral"
    },
    publishedResearch: [
      { citation: "Fava M et al. A Phase 1B, randomized, double blind, placebo controlled, multiple-dose escalation study of NSI-189 phosphate, a neurogenic compound. Mol Psychiatry. 2016." },
      { citation: "Papakostas GI et al. Phase 2 Trial of NSI-189 Phosphate in Major Depressive Disorder. 2020." }
    ],
    patentInfo: "Neuralstem patents exist; program shelved. Licensing or research-use production under academic exception.",
    wohlerScoring: {
      methodology: "Wohler Scoring System v1.0",
      benefitBreakdown: { therapeuticRationale: 20, evidenceQuality: 19, unmetMedicalNeed: 18, novelty: 12, universityInterest: 6 },
      harmBreakdown: { acuteToxicity: 3, chronicToxicity: 7, dependence: 2, schedulingRisk: 8 },
      netValue: 55,
      confidenceRationale: "HIGH — Phase I/II data exists; mechanism is unique."
    },
    priorityTier: "Tier 2",
    nextSteps: ["IP landscape review", "University partner in TBI / cognitive decline research", "Reference standard production"]
  }
};